import React, { useState } from "react";
import moment from "moment/moment";
import { useNavigate } from "react-router";
import { Plus as PlusIcon } from "react-feather";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SvgIcon from "@mui/material/SvgIcon";

import Footer from "src/components/wizard/Footer/Footer";
import Header from "../../../wizard/Header";
import Empty from "./Empty";
import getTotalReceivingUnitsLabel from "../Shipment/TotalReceivingUnits/getTotalReceivingUnitsLabel";
import useMutation from "../../../../hooks/useMutation";
import { UPDATE_SHIPMENT_MUTATION } from "../../../../constants/graphql/mutations";
import { serverFormatSeconds } from "../../../../utils/dates";
import { getShipmentValues } from "../../../../utils/shipment";
import OSAndDType from "../../../../components/capture/ReceivingWizard/OSAndD/OSAndDType";
import Item from "src/components/wizard/Item/Item";
import useQueryParams from "../../../../hooks/useQueryParams";
import OSAndDDetails from "./OSAndDDetails";
import OSAndDActions from "./OSAndDActions";
import SubmitShipmentModal from "./SubmitShipmentModal";
import ZeroQuantityErrorModal from "./ZeroQuantityErrorModal";
import useReceivingWizardFlow from "../hooks/useReceivingWizardFlow";
import WeightMismatchAlert from "../WeightMismatchAlert";

export default function OSAndD({ type, shipment, project, onClose, verticalLayout = false }) {
  const [openSubmitShipmentModal, setOpenSubmitShipmentModal] = useState(false);
  const [openZeroQuantityModal, setOpenZeroQuantityModal] = useState(false);
  const navigate = useNavigate();
  const params = useQueryParams();
  const openOSDType = params.get("openOSDType") === "true";
  const osdId = params.get("osdId");
  const [updateShipment] = useMutation(UPDATE_SHIPMENT_MUTATION);
  const totalPieces = shipment?.totalPieces || [];
  const input = getShipmentValues(shipment);
  const overagesShortagesDamages = shipment?.overagesShortagesDamages || [];
  const { osdBackStep, osdStepIndex, totalSteps } = useReceivingWizardFlow(project);

  const handleOpenOSDType = () => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(location.search);
    params.set("openOSDType", "true");
    navigate({ pathname, search: params.toString() });
  };

  const handleCloseOSDType = () => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(location.search);
    params.delete("openOSDType");
    navigate({ pathname, search: params.toString() });
  };

  const handleBack = () => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set("step", osdBackStep);
    navigate({ pathname, search: params.toString() });
  };

  const handleNext = () => {
    if (shipment.draft) {
      setOpenSubmitShipmentModal(true);
    } else {
      handleSubmit({ draft: false });
    }
  };

  const handleSubmit = ({ draft }) => {
    const values = { ...input, draft, finalStep: true };
    const hasZeroQuantity = shipment.shipmentItems.some(v => v.quantity === 0);
    const isOnsiteInventory = shipment.shipmentStatus === 'onsite_inventory';

    if (!draft && !isOnsiteInventory) {
      if (hasZeroQuantity) {
        setOpenZeroQuantityModal(true);
        return;
      }

      values.completeTime = shipment.completeTime
        ? shipment.completeTime
        : moment(new Date()).format(serverFormatSeconds);
      values.shipmentStatus = 'complete';
    }

    return updateShipment({
      variables: {
        id: shipment.id,
        input: values,
      },
    }).then(() => {
      const pathname = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      params.set("step", "confirmation");
      navigate({ pathname, search: params.toString() });
    });
  };

  const handleEdit = id => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set("osdId", id);
    navigate({ pathname, search: params.toString() });
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
      <Header title="OS&D (Warehouse Exceptions)" info={getTotalReceivingUnitsLabel(totalPieces)} />
      <WeightMismatchAlert shipment={shipment} />
      <Box p={2} flexGrow={1}>
        <Box display="flex" flexDirection="column" alignItems="center">
          {overagesShortagesDamages.length === 0 ? (
            <Box mt={3} display="flex" flexDirection="column" alignItems="center">
              <Empty />
            </Box>
          ) : (
            <Box mb={5} width="100%">
              {overagesShortagesDamages.map(v => (
                <Item
                  value={{ ...v.shipmentItem, attachments: v.attachments }}
                  info={
                    <Box color="red" fontWeight={500}>
                      {v.osdType}, QTY: {v.osdQuantity}
                    </Box>
                  }
                  onClick={() => handleEdit(v.id)}
                />
              ))}
            </Box>
          )}
          <Box mt={1}>
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={handleOpenOSDType}
              startIcon={
                <SvgIcon fontSize="small">
                  <PlusIcon />
                </SvgIcon>
              }
            >
              Add OS&D
            </Button>
            {openOSDType && <OSAndDType onClose={handleCloseOSDType} shipment={shipment} />}
            {osdId && (
              <OSAndDDetails
                osdId={osdId}
                osd={overagesShortagesDamages.find(v => v.id === osdId)}
                shipment={shipment}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box mt={2}>
        <Footer
          step={osdStepIndex}
          steps={totalSteps}
          startTime={type === "approve" || verticalLayout ? null : shipment?.startTime}
          completeTime={type === "approve" || verticalLayout ? null : shipment?.completeTime}
        >
          <OSAndDActions
            type={type}
            onBack={handleBack}
            onNext={handleNext}
            onClose={onClose}
            shipment={shipment}
            verticalLayout={verticalLayout}
          />
        </Footer>
      </Box>
      {openSubmitShipmentModal && (
        <SubmitShipmentModal
          onClose={() => setOpenSubmitShipmentModal(false)}
          onSubmit={() => handleSubmit({ draft: false })}
          onSaveDraft={() => handleSubmit({ draft: true })}
        />
      )}
      <ZeroQuantityErrorModal open={openZeroQuantityModal} onClose={handleBack} />
    </Box>
  );
}
